import { Fragment, type HTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'

// ---------------------------------------------------------------------------
// DefinitionList
//
// Presentational definition-list component. Renders a semantic <dl> grid
// using the .dl family from shared/styles/components.css (token-backed).
//
// API (contracts/component-library.md):
//   items[]   — array of { term, detail } pairs
//   term      — the label (dt); caller-supplied, already i18n'd
//   detail    — the value (dd); string or arbitrary ReactNode
//
// Passes through all standard <dl> HTML attributes (className, aria-label,
// data-*, etc.).
//
// Safe for the public sessionless register bundle: no data fetching, no auth
// logic, no privileged strings.
// ---------------------------------------------------------------------------

export type DefinitionItem = {
  /** The term label — rendered as a <dt>. Caller i18n's this. */
  term: ReactNode
  /** The detail value — rendered as a <dd>. May be a string or ReactNode. */
  detail: ReactNode
}

export type DefinitionListProps = {
  /** List of term/detail pairs to render. */
  items: DefinitionItem[]
} & Omit<HTMLAttributes<HTMLDListElement>, 'children'>

/**
 * Definition-list grid. Presentational — props in, UI out.
 *
 * Renders a <dl class="dl"> that uses a two-column grid layout:
 * the first column is max-content-wide (for terms), the second is 1fr
 * (for details). Styling is fully delegated to .dl in components.css.
 *
 * Usage:
 *   <DefinitionList
 *     items={[
 *       { term: t('address'), detail: applicant.address },
 *       { term: t('email'),   detail: applicant.email },
 *     ]}
 *   />
 */
export function DefinitionList({ items, className, ...rest }: DefinitionListProps) {
  return (
    <dl className={clsx('dl', className)} {...rest}>
      {items.map((item, index) => (
        // React.Fragment with key renders no DOM node — dt and dd land as
        // direct children of the <dl>, letting the CSS grid pick them up.
        // Using index as key is safe: the list is static (no reorder).
        <Fragment key={index}>
          <dt>{item.term}</dt>
          <dd>{item.detail}</dd>
        </Fragment>
      ))}
    </dl>
  )
}
